import { Capacitor } from '@capacitor/core'

declare const __APP_WEB_VERSION__: string
declare const __APP_WEB_BUILD_ID__: string

export interface PublishedWebBuild {
  version: string
  buildId?: string
}

export interface WebUpdateCheck {
  currentVersion: string
  publishedVersion: string
  updateAvailable: boolean
}

const currentVersion = typeof __APP_WEB_VERSION__ !== 'undefined' ? __APP_WEB_VERSION__ : '0.0.0'
const currentBuildId = typeof __APP_WEB_BUILD_ID__ !== 'undefined' ? __APP_WEB_BUILD_ID__ : ''
const RELOAD_KEY = 'shangan:web-update-reload'

export function supportsWebUpdates() {
  return import.meta.env.PROD && !Capacitor.isNativePlatform() && 'serviceWorker' in navigator
}

async function publishedBuild(): Promise<PublishedWebBuild> {
  const response = await fetch(`/version.json?update=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
  })
  if (!response.ok) throw new Error('无法获取网页版版本信息')
  const value = await response.json() as Partial<PublishedWebBuild>
  if (!value.version) throw new Error('网页版版本信息无效')
  return { version: value.version, buildId: value.buildId }
}

export function isPublishedWebBuildDifferent(build: PublishedWebBuild, version: string, buildId: string) {
  if (build.version !== version) return true
  return Boolean(build.buildId && buildId && build.buildId !== buildId)
}

function isDifferent(build: PublishedWebBuild) {
  return isPublishedWebBuildDifferent(build, currentVersion, currentBuildId)
}

async function serviceWorkerRegistration() {
  if (!supportsWebUpdates()) return null
  // updateViaCache:none 强制更新检查绕过 HTTP 缓存，避免浏览器长期复用旧 sw.js。
  return navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
}

export async function checkWebUpdate(): Promise<WebUpdateCheck> {
  const [build, registration] = await Promise.all([
    publishedBuild(),
    serviceWorkerRegistration(),
  ])
  await registration?.update()
  if (!isDifferent(build)) sessionStorage.removeItem(RELOAD_KEY)
  return {
    currentVersion,
    publishedVersion: build.version,
    updateAvailable: isDifferent(build),
  }
}

function waitForActivation(registration: ServiceWorkerRegistration) {
  return new Promise<void>(resolve => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      resolve()
    }
    navigator.serviceWorker.addEventListener('controllerchange', finish, { once: true })
    const worker = registration.waiting || registration.installing
    if (!worker) {
      finish()
      return
    }
    if (registration.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    worker.addEventListener('statechange', () => { if (worker.state === 'activated') finish() })
    window.setTimeout(finish, 8_000)
  })
}

/** 激活最新 Service Worker，并只重载一次，避免弱网或部署切换期间形成刷新循环。 */
export async function applyWebUpdate(force = false) {
  if (!supportsWebUpdates()) return false
  const build = await publishedBuild()
  const buildKey = `${build.version}:${build.buildId || ''}`
  if (!force && !isDifferent(build)) return false
  if (!force && sessionStorage.getItem(RELOAD_KEY) === buildKey) return false

  const registration = await serviceWorkerRegistration()
  if (!registration) return false
  sessionStorage.setItem(RELOAD_KEY, buildKey)
  const activated = waitForActivation(registration)
  await registration.update()
  if (registration.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  await activated
  window.location.reload()
  return true
}

/** 每次打开 Web 端均核对服务器构建标识；发现新架构后静默接管并刷新。 */
export async function refreshWebAppOnLaunch() {
  if (!supportsWebUpdates()) return
  try {
    const result = await checkWebUpdate()
    if (result.updateAvailable) await applyWebUpdate(false)
  } catch {
    // 离线时继续使用当前缓存；下次打开或手动检查时重试。
  }
}
